export const sharedContent = Object.freeze({
  newsletter: {
    heading: "Stay close to the work",
    body: "Practical perspectives on improving business operations, choosing technology and checking whether a change delivered the result it was meant to achieve.",
    cta: { label: "Subscribe on LinkedIn", target: "newsletter" },
  },
  footer: {
    description: "LevelUp360 helps businesses release capacity, reduce cost and improve service through better processes, automation and the right use of AI. We establish the cause, carry the agreed change through implementation and measure the result.",
  },
});

export const homePage = Object.freeze({
  path: "/",
  seoTitle: "LevelUp360 | Business Improvement, Automation & AI",
  seoDescription: "Reduce cost, handle more work and improve service. We assess where AI and automation fit, implement the right change and measure the result.",
  hero: {
    eyebrow: "Business improvement, automation and AI",
    headline: "Handle more work without adding people at the same rate.",
    subheading: "We find what is consuming your team's time, establish where AI, automation or a simpler change would help, and carry the agreed solution through implementation. Then we measure whether cost, capacity or service improved.",
    image: "/assets/images/home-hero.jpg",
    imagePosition: "center 46%",
    ctas: [
      { label: "Discuss what you want to improve", target: "bookCall", style: "primary" },
      { label: "See how we help", target: "/services/", style: "secondary" },
    ],
  },
  introduction: {
    heading: "Your team is busy. What is holding the business back?",
    paragraphs: [
      "Your team is stretched, costs are under pressure and the business needs to handle more work. You may be considering AI to help, or already using it without a clear improvement in cost, capacity or service.",
      "We work with leaders and the people doing the work to establish what is causing the problem and compare practical options. Where AI would help, we establish what must change for it to work reliably. Where a simpler process, better use of existing systems or ordinary automation would work better, we recommend that instead. The agreed change is designed, implemented and measured.",
    ],
    points: [
      "Find where time, capacity or money is being lost.",
      "Establish what is causing the delay, rework or dependence on key people.",
      "Compare practical options and explain their likely benefit, cost and limits.",
      "Give the accountable people a recommendation they can question and approve.",
      "Carry the agreed change into use and check the result against the starting point.",
    ],
  },
  journey: {
    heading: "From first decision to a working solution",
    stages: [
      {
        number: "01",
        id: "stage-1",
        title: "Decide what to change and where to start",
        problem: "Establish which problems matter most, what is causing them and which change is worth pursuing.",
        intervention: "",
        outcome: "Clear priorities, practical options and a reasoned recommendation for the next decision.",
        href: "/services/#aidecision",
      },
      {
        number: "02",
        id: "stage-2",
        title: "Redesign how the selected work gets done",
        problem: "Investigate the selected work and design a simpler way to do it, with clear responsibilities and the right use of existing systems and technology.",
        intervention: "",
        outcome: "An agreed design for how the work should happen, including decisions, exceptions and responsibilities.",
        href: "/services/#vantage",
      },
      {
        number: "03",
        id: "stage-3",
        title: "Design the solution and plan implementation",
        problem: "Make the proposed change clear enough to approve, price and deliver, including the requirements, dependencies and checks it needs.",
        intervention: "",
        outcome: "A practical design and implementation plan for the people approving and delivering the change.",
        href: "/services/#aicontrol",
      },
      {
        number: "04",
        id: "stage-4",
        title: "Deliver and measure the change",
        problem: "Carry the agreed change through implementation, testing and handover, then compare the operating result with the agreed baseline.",
        intervention: "",
        outcome: "A working change, clear ownership and evidence of what improved and what still needs attention.",
        href: "/services/#delivery",
      },
    ],
    closing: "Start where you need help. We use credible work you have already completed and agree a specific scope and useful outcome before starting. Continuing to another stage is a separate decision.",
    cta: { label: "Find your starting point", target: "/services/" },
  },
  govern360: {
    heading: "Govern360",
    body: "Govern360 is a product currently in development at LevelUp360. For enquiries, contact us.",
    cta: { label: "About Govern360", target: "/products/#govern360" },
  },
  fit: {
    heading: "Is LevelUp360 right for your organisation?",
    body: "We are most useful when delays, rework, rising costs or dependence on experienced people are limiting an important part of your back-office operation. That may be finance, administration, compliance, order processing or internal services. You need an owner who can act on the findings and a problem worth resolving; you do not need to arrive with a chosen technology or a fully defined solution. The work is led by Manuel Tomas Estarlich, drawing on more than 20 years across business change, process automation, solution design and delivery.",
    note: "The scope should fit the problem. A straightforward improvement should stay straightforward; more complex changes need the appropriate expertise and checks.",
    cta: { label: "Discuss what you want to improve", target: "/contact/" },
    fundingLink: { label: "Funding for eligible Irish businesses", target: "/services/#funding" },
  },
});
